import { ApplicationRoute } from './../../constants/routes';
import Controller, { OnBeforeFetch, OnBlur, OnClear, OnClick, OnMount, OnReadSuccess } from '@philips-internal/oswald-ui-core/lib/client';
import BaseController from '../BaseController';
import { HttpService, MessageService,  } from '@philips-internal/oswald-ui-core/lib/services';
import { Endpoint } from '../../constants/endpoints';
import { Inject } from '@odinjs/odin';
import { ClickEvent, FetchEvent, MountEvent, ReadEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { AutocompleteAPI, AvatarAPI, BarcodeAPI, BarcodeData, BaseToastProps, FetchAPI, FieldAPI, FilterAPI, ManualBarcodeReason, MapperAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { MedicineAndSolutionsParams } from '../adep/eletronic-prescription/medicine-and-solutions/medicine-and-solutions-params';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import './search-patient-emar-controller.css';
import { isFunction } from '@philips-internal/oswald-ui-utils';
import { Patient } from '../../interfaces/exam-collect/patiente-data-props';
import { Value } from '@philips-internal/oswald-ui-core';
import { SearchPatientEmarAliases } from '../../codegen/constants/aliases';
import { ImageUtils } from '../../utils/image-utils';
import {SearchPatientEmarAliases as Alias, FragmentNames} from '../../codegen';
import BarcodeService from '../../services/barcode-service';
import moment from 'moment';

interface EncounterStatusResponse {
  ieAtendValido: string;
}

interface PatientMapper {
  cdPessoaFisica: string;
  genero: string;
  idade: string;
  nome: string;
  nrAtendimento: number;
  photo: string;
  bornDate: string;
}

class SearchPatientEmarView {
  static viewState = false;
}

const toastErrorId = 'search-patient-controller.no-access-to-encounter';

@Controller({ component: Alias.LAYOUT, fragment: FragmentNames.SEARCH_PATIENT_EMAR })
class SearchPatientEmarController extends BaseController {

  @Inject(SearchPatientEmarAliases.FILTER_PATIENTS)
  private filterPatients!: FilterAPI;

  @Inject('filter-field-encounter')
  private encounterNumberField!: FieldAPI;

  @Inject(Alias.FIELD_DEPARTMENTS)
  protected departmentsField!: FieldAPI;

  @Inject(Alias.AUTOCOMPLETE_DEPARTMENTS)
  private autoCompleteDepartments!: AutocompleteAPI;

  @Inject(Alias.BARCODE_COMPONENT)
  private barcodeComponentApi!: BarcodeAPI;

  @Inject(Alias.AVATAR_EMAR)
  private avatarAPI!: AvatarAPI;

  @OnMount(Alias.AVATAR_EMAR)
  handleMountAvatar(): void {
    const desktopNotSupported = '../../../resources/assets/desktop_not_supported.svg';
    this.avatarAPI.setImage(desktopNotSupported);
  }

  @OnMount(SearchPatientEmarAliases.BARCODE_COMPONENT)
  handleMountBarcode(event: MountEvent): void {
    const barcodeApi = event.target as BarcodeAPI
    barcodeApi.setDefaultReadType(BarcodeService.getDefaultMethodOfReadingBarcode())
    this.getReasonsToBarcodeManual();
  }

  private async getReasonsToBarcodeManual(): Promise<void> {
    await HttpService.get(Endpoint.EMAR_SEARCH_PATIENT)
      .then((response) => {
        const responseBody = response.body as ManualBarcodeReason[];
        const responseBodyRefactored = responseBody?.map((item) => {
          return {
            description: item.description,
            id: String(item.id)
          }
        })

        this.barcodeComponentApi?.setData(responseBodyRefactored);
      })
  }

  @OnClear(SearchPatientEmarAliases.FILTER_PATIENTS)
  onFilterClear(): void {
    this.filterPatients.clear();
    this.autoCompleteDepartments.setValue({} as Record<string, Value>)
  }

  @OnMount(SearchPatientEmarAliases.PATIENT_FETCH_MAPPER)
  patientFetchMapper(onMountEvent: MountEvent): void {
    const mapperEvent = onMountEvent.target as MapperAPI;

    mapperEvent.addCustomMapping('subtitle', data  => {
      const dataItem = data as unknown as PatientMapper;
      const bornDate = moment(dataItem.bornDate).format('DD-MM-YYYY');
      return `${bornDate} / ${dataItem.genero} / ${dataItem.nrAtendimento}`;
    });

    mapperEvent.addCustomMapping('photo', data  => {
      const dataItem = data as unknown as PatientMapper;
      return ImageUtils.getImageAvatar(dataItem.photo as string)
    });
  }

  goToInformationBar(cdPaciente: string, nrAtendimento: string): void {
    HttpService.get(Endpoint.USER_HAS_EMR_ACCESS_TO_ENCOUNTER_ESTABLISHMENT, { cdPaciente, encounterId: nrAtendimento })
      .then(() => {
        applicationContext.redirect(
          ApplicationRoute.ELECTRONIC_MEDICATION_ADMINISTRATION +
          ApplicationRoute.SEACH_PATIENT_EMAR +
          ApplicationRoute.ADEP_ELETRONIC_SERVICE_REASON, { encounterId: nrAtendimento, patientId: cdPaciente }
        );
      })
      .catch(() => {
        this.addEncounterNumberFieldMessage(MessageService.translate('text.no-access-to-encounter'))
      });
  }

  getPatientAndRedirect(nrAtendimento: string): void {
    const queryParams = `?nrAtendimento=${nrAtendimento}&page=0&limit=12`;
    const route = Endpoint.GET_PATIENT_LIST + queryParams;

    HttpService.get(route).then(resp => {
      const body = resp.body as Patient[];
      if (body.length === 0) {
        throw new Error();
      }
      const cdPaciente = body[0].cdPessoaFisica;

      this.goToInformationBar(cdPaciente, nrAtendimento);
    }).catch(() => {
      this.addEncounterNumberFieldMessage(MessageService.translate('text.no-access-to-encounter'));
    })
  }

  validateBarcode(event: ReadEvent<BarcodeData>): void{
    const {preventDefault} = event;
    if (isFunction(preventDefault)) {
      preventDefault();
    }
  }

  errorMessage(): void{
    const errorMenssage = MessageService.translate('tws.search-patient-ec.invalid-code-error');
    const titleMessage = MessageService.translate('text.message-alert-attention');
    ToastService.close('custom-toast');
    ToastService.open({
      dismissible: true,
      id: 'custom-toast',
      level: ToastLevel.ERROR,
      message: errorMenssage,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      title: titleMessage,
      type: ToastType.BUBBLE,
    });
  }

  @OnClick({ target: Alias.PATIENT_FETCH_DECK })
  onCardClick(event: ClickEvent): void {
    const { cdPessoaFisica: cdPaciente, nrAtendimento } = event.value;
    this.filterPatients.persistState(true);
    MedicineAndSolutionsParams.params = { modalIsVisible: 'S' };
    HttpService.get(Endpoint.USER_HAS_EMR_ACCESS_TO_ENCOUNTER_ESTABLISHMENT, { cdPaciente, encounterId: nrAtendimento })
      .then(() => {
        applicationContext.redirect(ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION, { encounterId: nrAtendimento, patientId: cdPaciente });
      });
    SearchPatientEmarView.viewState = true;
  }

  @OnReadSuccess({ target: Alias.BARCODE_COMPONENT })
  onReadSucesstBarCode(event: ReadEvent<BarcodeData>): void {
    const barcode = event.target as BarcodeAPI;
    this.validateBarcode(event);

    const queryParams = {
      limit: 12,
      nrAtendimento: event.item.value.trim(),
      page: 0,
    }
    barcode.close();
    barcode.setEnabled(false);

    HttpService.get(Endpoint.GET_PATIENT_LIST, queryParams).then(resp=>{
      const bodyPatient = resp.body as Patient[];
      this.barcodeComponentApi.setEnabled(true);

      if (bodyPatient.length === 0) {
        this.errorMessage();
        barcode.setOpen(true);
        return;
      }

      const patientId = bodyPatient[0].cdPessoaFisica;
      const body = {
        dsReason: event.item.description ?? '',
        nrSequence: event.item.reason,
        readingMode: 'D'
      }

      HttpService.post(
        Endpoint.POST_NEW_REASON_JUSTIFICATION, {
          encounterId:
          event.item.value.trim(),
          patientId,
        }, { body }
      );

      MedicineAndSolutionsParams.params = { modalIsVisible: 'S' };
      applicationContext.redirect(ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION, {
        encounterId: String(event.item.value.trim()),
        patientId,
      }
      );
    }).catch(() => {
      barcode.setEnabled(true);
      barcode.setOpen(true);
    })
  }

  @OnBlur({ target: 'input-encounter' })
  async onFilterFieldBlur(): Promise<void> {
    const validateResult = await this.encounterNumberField.validate();
    const nrAtendimento = this.encounterNumberField.getData().nrAtendimento as string;
    if (validateResult) {
      this.checkEncounterNumberStatus(nrAtendimento);
    }
  }

  checkEncounterNumberStatus(nrAtendimento: string): void {
    HttpService.get(Endpoint.ENCOUNTER_STATUS, { nrAtendimento }).then((resp) => {
      const respStatus = resp.body as EncounterStatusResponse;
      const validStatus = respStatus.ieAtendValido;

      switch (validStatus) {
        case 'A': this.addEncounterNumberFieldMessage(MessageService.translate('tws.search-patient-ec.discharge-data-error'));
          break;
        case 'V': this.getPatientAndRedirect(nrAtendimento);
          break;
        case 'N': this.addEncounterNumberFieldMessage(MessageService.translate('tws.search-patient-ec.invalid-code-error'));
          break;
        default:
          break;
      }
    }).catch(() => { })
  }

  addEncounterNumberFieldMessage(message: string, name?: string): void {
    try {
      const validatorObj = {
        message: MessageService.translate(message),
        name: name || '',
        valid: false
      }
      const validatorName = 'encounter-code-status-validator';
      this.encounterNumberField.addLocalValidator(validatorName, async () => ([validatorObj]));
      this.encounterNumberField.validate();
      this.encounterNumberField.removeLocalValidator(validatorName);

    } finally {
      this.showErrorToast(message);
    }
  }

  showErrorToast(message: string): void {
    const toast: BaseToastProps = {
      dismissible: true,
      id: toastErrorId,
      level: ToastLevel.ERROR,
      message: MessageService.translate(message),
      showIcon: true,
      timeout: 5000,
      type: ToastType.BUBBLE
    };
    ToastService.open(toast)
  }

  @OnBeforeFetch({ target: Alias.FETCH_DEPARTMENTS })
  onBeforeFetchDepartment(evt: FetchEvent): void {
    const event = evt.target as FetchAPI;
    if (evt) {
      const params = {
        dsSetorAtendimento: this.departmentsField.getValue(),
        emar: true
      }

      event.setParameters(params);
    }
  }

}

export default SearchPatientEmarController;
